import { useCallback, useEffect, useMemo, useState } from 'react';
import { T } from '../../components/text/TextEdit';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { FlaskConical } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { api } from '../../lib/api';

// Rule 8 — QA config: the dedicated test tenant + the FREE model every QA generation is forced onto
// (fail-closed server-side). is_admin-gated RPCs (get_qa_config / set_qa_config). `models` are the
// model_prices rows, used to pick qa_model_id.
export function QaConfigCard({ models = [] }) {
  const { t } = useTranslation();
  const [cfg, setCfg] = useState({ qa_tenant_id: '', qa_model_id: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await api.getQaConfig();
      setCfg({ qa_tenant_id: d.qa_tenant_id || '', qa_model_id: d.qa_model_id || '' });
    } catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const modelOptions = useMemo(() => {
    const ids = models.map((m) => m.model_id);
    if (cfg.qa_model_id && !ids.includes(cfg.qa_model_id)) return [cfg.qa_model_id, ...ids];
    return ids;
  }, [models, cfg.qa_model_id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.setQaConfig({
        qa_tenant_id: cfg.qa_tenant_id.trim() || null,
        qa_model_id: cfg.qa_model_id.trim() || null,
      });
      toast.success(t('qaConfig.saved'));
      await load();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 flex flex-col gap-4" data-testid="qa-config-card">
      <div className="flex items-center gap-2">
        <FlaskConical className="h-4 w-4 text-muted-foreground" />
        <h3 className="font-heading text-sm font-semibold text-foreground"><T k="qaConfig.title" /></h3>
      </div>
      <p className="text-xs text-muted-foreground -mt-2">
        {t('qaConfig.intro')}
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs tracking-[0.14em] uppercase font-bold text-muted-foreground" htmlFor="qa-tenant">
            {t('qaConfig.tenantId')}
          </label>
          <Input
            id="qa-tenant"
            value={cfg.qa_tenant_id}
            onChange={(e) => setCfg({ ...cfg, qa_tenant_id: e.target.value })}
            placeholder={t('qaConfig.tenantPlaceholder')}
            className="text-sm font-data"
            data-testid="qa-config-tenant-input"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs tracking-[0.14em] uppercase font-bold text-muted-foreground"><T k="qaConfig.model" /></label>
          <Select value={cfg.qa_model_id || '__none__'} onValueChange={(v) => setCfg({ ...cfg, qa_model_id: v === '__none__' ? '' : v })}>
            <SelectTrigger data-testid="qa-config-model-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__" data-testid="qa-config-model-none"><T k="qaConfig.none" /></SelectItem>
              {modelOptions.map((id) => (
                <SelectItem key={id} value={id} data-testid={`qa-config-model-${id}`}>{id}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Button onClick={save} disabled={saving} data-testid="qa-config-save">
          {saving ? t('providersEditor.saving') : t('qaConfig.save')}
        </Button>
      </div>
    </div>
  );
}
